/**
 * The item cost matrix's cells, headless (catalogue third-party fulfilment
 * journey A; Ben's layout decisions, 24 Sep): one key, one line, four marks
 * meaning the same on every row type, and every cell is exactly the figure
 * (or "—") then "mark · message". No preamble, cause lines, attribution or
 * status chips — the full reason and history live on the cost-pair page.
 *
 * Row types: "In-house" — one cell, one figure: the checked figure and the
 * check's verdict whenever a check exists, else the engine's cost, not yet
 * checked; the cached Tharstern estimate and its freshness while live
 * costing is paused — and one row per supplier route. A cell is either
 * checked (coloured) or not (grey); there is no versioning. Which kind a
 * cell is, and a supplier cost's freshness, arrive decided by the backend;
 * this file only words and marks them.
 */

import type {
  CheckSubstitution,
  CostCheckCellState,
  CostLineRef,
} from "../data/cost-engine-validation-types";
import type {
  InHouseCell,
  InHouseCheckCounts,
  SupplierCostFreshness,
  SupplierRouteCostCell,
} from "../data/fulfilment-route-types";
import { formatCurrencyGbp } from "./formatters";
import { formatAge } from "./fulfilment-route-presentation";

export type CellMark = "green" | "amber" | "red" | "grey";

/** The key: four marks, the same meaning on every row type. */
export const CELL_MARKS: Record<
  CellMark,
  { symbol: "●" | "○"; label: string; meaning: string }
> = {
  green: { symbol: "●", label: "Green", meaning: "good" },
  amber: { symbol: "●", label: "Amber", meaning: "attention" },
  red: { symbol: "●", label: "Red", meaning: "wrong or errored" },
  grey: { symbol: "○", label: "Grey", meaning: "no figure yet" },
};

export const CELL_MARK_ORDER: CellMark[] = ["green", "amber", "red", "grey"];

export const IN_HOUSE_ROW_LABEL = "In-house";

/** A mark and its message — a cell's line 2, and the cost-pair page's
 *  header. */
export interface MarkMessage {
  mark: CellMark;
  message: string;
}

/** One matrix cell as the page renders it: line 1 the figure, line 2 the
 *  mark and its message. */
export interface MatrixCellDisplay extends MarkMessage {
  figure: string;
}

const NO_FIGURE = "—";

function money(pence: number | null): string {
  return pence == null ? NO_FIGURE : formatCurrencyGbp(pence / 100, 2);
}

/** "+£0.15" / "−£3.10" — ours minus Tharstern, a true minus sign. */
export function formatSignedPence(pence: number): string {
  const amount = formatCurrencyGbp(Math.abs(pence) / 100, 2);
  if (pence > 0) return `+${amount}`;
  if (pence < 0) return `−${amount}`;
  return amount;
}

/** "finishing run 801", "ink 5351", "paper" — a cost line in a few words:
 *  its component and the Tharstern cost centre number (0 is the
 *  cost-centre-less line, which has none to give). */
export function formatCostLineCause(costLine: CostLineRef): string {
  const component = costLine.component.toLowerCase();
  return costLine.costCentreId
    ? `${component} ${costLine.costCentreId}`
    : component;
}

/** "Tharstern substituted WWW_UNC_400 → WWW_UNC_400_ALT". Only a value the
 *  item's mapping set is ever a substitution (decision 81) — a Press or
 *  routing difference arrives as an ordinary difference. */
export function formatSubstitution(substitution: {
  sent: string;
  used: string;
}): string {
  return `Tharstern substituted ${substitution.sent} → ${substitution.used}`;
}

/** A check's verdict as a mark and message: Matches; the signed difference
 *  and its cause; or, when the backend served a substitution, that — red
 *  whatever the price verdict (a substituted check can still match on
 *  price). Grey when there is no check, or one is running. Shared by the
 *  grid and the cost-pair page, so the two can never say different things. */
export function describeCheckVerdict(check: {
  // A check's verdict is never one of the cached-estimate freshness states
  // the wire's cell state also carries for a paused item's cache.
  state: Exclude<
    CostCheckCellState,
    "fresh" | "stale" | "failed" | "pending" | "held_back"
  >;
  ourPence: number | null;
  tharsternPence: number | null;
  costLine: CostLineRef | null;
  isSubstitution: boolean;
  substitution: CheckSubstitution | null;
}): MarkMessage {
  if (check.state === "checking") return { mark: "grey", message: "Checking…" };
  if (check.state === "grey")
    return { mark: "grey", message: "Not yet checked" };
  if (check.isSubstitution && check.substitution) {
    return { mark: "red", message: formatSubstitution(check.substitution) };
  }
  switch (check.state) {
    case "green":
      return { mark: "green", message: "Matches" };
    case "amber":
    case "red": {
      const difference =
        check.ourPence != null && check.tharsternPence != null
          ? formatSignedPence(check.ourPence - check.tharsternPence)
          : null;
      const cause = check.costLine ? formatCostLineCause(check.costLine) : null;
      const message =
        [difference, cause].filter(Boolean).join(", ") ||
        (check.state === "amber" ? "Small differences" : "Mismatch");
      return { mark: check.state, message };
    }
  }
}

type CachedEstimateCell = Extract<InHouseCell, { status: "cached_estimate" }>;

/** A paused item's cached Tharstern estimate, marked by the freshness the
 *  backend classified (never a verdict — nothing is compared). ``heldBack``
 *  is amber: a known-rejected combination, kept in the dictionary and still
 *  orderable via a supplier route, but never sent to Tharstern. */
const CACHED_ESTIMATE_MARKS: Record<CachedEstimateCell["freshness"], CellMark> =
  {
    fresh: "green",
    stale: "amber",
    failed: "red",
    pending: "grey",
    held_back: "amber",
  };

/** Why a stale estimate is stale when its age alone does not say so. */
const STALE_BECAUSE: Record<"spec" | "schema", string> = {
  spec: "item changed since",
  schema: "Tharstern changed since",
};

/** Decision 75 (Ben, 25 Sep): the backend's classification is the mark and
 *  the text carries the estimate's age — "Cached estimate · fresh, 2 days
 *  old", "Cached estimate · 34 days old", "Cached estimate · 5 days old ·
 *  item changed since". A failed refresh says why; a never-costed pair says
 *  so. A held-back pair (#99, 26 Sep) reads "Held back from Tharstern · <note>" —
 *  a distinct message, not the "Cached estimate" prefix, since nothing was
 *  ever fetched or is ever going to be. */
export function describeCachedEstimate(
  cell: CachedEstimateCell,
  now: Date,
): MarkMessage {
  const mark = CACHED_ESTIMATE_MARKS[cell.freshness];
  if (cell.freshness === "held_back") {
    return {
      mark,
      message: `Held back from Tharstern · ${cell.reason ?? "see the item file"}`,
    };
  }
  if (cell.freshness === "pending") return { mark, message: "Not yet costed" };
  if (cell.freshness === "failed") {
    return { mark, message: cell.reason ?? "Tharstern estimate failed" };
  }
  const age = cell.fetchedAt ? formatAge(cell.fetchedAt, now) : null;
  if (cell.freshness === "fresh") {
    return {
      mark,
      message: age
        ? `Cached estimate · fresh, ${age}`
        : "Cached estimate · fresh",
    };
  }
  const because =
    cell.staleCause === "spec" || cell.staleCause === "schema"
      ? STALE_BECAUSE[cell.staleCause]
      : null;
  return {
    mark,
    message: ["Cached estimate", age ?? "stale", because]
      .filter(Boolean)
      .join(" · "),
  };
}

/** The in-house cell: one figure and what it is. */
export function describeInHouseCell(
  cell: InHouseCell,
  isChecking: boolean,
  now: Date,
): MatrixCellDisplay {
  if (cell.status === "no_in_house_route") {
    return { figure: NO_FIGURE, mark: "grey", message: "Not made in-house" };
  }
  const figure = money(cell.figurePence);
  if (isChecking) return { figure, mark: "grey", message: "Checking…" };
  switch (cell.status) {
    case "checked":
      return {
        figure,
        ...describeCheckVerdict({
          state: cell.verdict,
          ourPence: cell.figurePence,
          tharsternPence: cell.tharsternPence,
          costLine: cell.costLine,
          isSubstitution: cell.isSubstitution,
          substitution: cell.substitution,
        }),
      };
    case "not_checked":
      return { figure, mark: "grey", message: "Not yet checked" };
    case "cached_estimate":
      return { figure, ...describeCachedEstimate(cell, now) };
  }
}

/** The figure an in-house cell holds, or null — none for a supplier-only
 *  item (#71). */
export function inHouseFigurePence(cell: InHouseCell): number | null {
  return cell.status === "no_in_house_route" ? null : cell.figurePence;
}

/** Whether a cell has anything to show on the cost-pair page — only a
 *  coloured cell links there; a grey one has nothing yet. */
export function isColouredMark(mark: CellMark): boolean {
  return mark !== "grey";
}

const SUPPLIER_MARKS: Record<
  Exclude<SupplierCostFreshness, "errored">,
  MarkMessage
> = {
  live: { mark: "green", message: "Live" },
  expiring: { mark: "amber", message: "Expiring" },
  expired: { mark: "red", message: "Expired" },
  not_yet_quoted: { mark: "grey", message: "Not yet quoted" },
  unmapped: { mark: "grey", message: "Unmapped" },
};

/** A supplier route's cell: its freshness, or the mapping error text on
 *  its own. The figure shows only while live or expiring. */
export function describeSupplierCell(
  cell: SupplierRouteCostCell,
): MatrixCellDisplay {
  if (cell.freshness === "errored") {
    // The run's error text on its own — the reason carries it.
    return {
      figure: NO_FIGURE,
      mark: "red",
      message: cell.reason ?? "Errored",
    };
  }
  return { figure: money(cell.netPence), ...SUPPLIER_MARKS[cell.freshness] };
}

/** "3 matches · 2 differences · 1 not yet checked · 1 substitution" —
 *  zero counts left out, "No checks yet" when there are none. */
export function formatInHouseCheckCounts(counts: InHouseCheckCounts): string {
  const parts = [
    [counts.matches, "match", "matches"],
    [counts.differences, "difference", "differences"],
    [counts.notYetChecked, "not yet checked", "not yet checked"],
    [counts.substitutions, "substitution", "substitutions"],
  ] as const;
  const shown = parts
    .filter(([count]) => count > 0)
    .map(([count, one, many]) => `${count} ${count === 1 ? one : many}`);
  return shown.length > 0 ? shown.join(" · ") : "No checks yet";
}

/** "Solopress figures include delivery." — the key's one footnote. */
export function formatDeliveryFootnote(routeLabels: string[]): string {
  return `${routeLabels.join(" and ")} figures include delivery.`;
}

/** The accessible name of a cell: its row, quantity, figure and message. */
export function formatMatrixCellLabel(
  rowLabel: string,
  quantity: number,
  display: MatrixCellDisplay,
): string {
  const figure = display.figure === NO_FIGURE ? "no figure" : display.figure;
  return `${rowLabel}, qty ${quantity.toLocaleString("en-GB")}: ${figure} · ${CELL_MARKS[display.mark].label} · ${display.message}`;
}

/** "Check 350gsm silk, standard at 500 now?" / "…at all quantities now?" */
export function formatCheckNowTitle(
  variantLabel: string,
  quantity: number | "all",
): string {
  const at =
    quantity === "all" ? "all quantities" : quantity.toLocaleString("en-GB");
  return `Check ${variantLabel} at ${at} now?`;
}
